// Dashboard adapters (FRONTEND_RULES §2, §7).
//
// `mapDashboardResponse` is the ONLY read boundary: it maps the backend /dashboard
// contract (already camelCase) onto the presentation model the widgets consume.
//
// There used to be a second boundary, `mapAPIToUI`, which deep-camelized a separate
// snake_case mock that lived in the frontend. Two shapes for one screen meant they
// drifted — the mock lacked the hero's newer fields, so mock mode rendered a degraded
// hero (no tenure pill, no chart-view toggle, "growth.undefined" rows) while live mode
// was correct, and nothing failed. The dashboard's fixture data now lives in the
// backend and arrives by the production path, so both that mapper and the mock are gone.
//
// `mapUIToAPI` documents the write direction where `org_id` is injected (never in a
// component); unused by the read-only dashboard today but kept so future mutations
// follow the pattern.
import { activityRows } from '../../../utils/activity';
import { wholePercents } from '../../../utils/holdings';
// One pending-balance model for the whole app (FM-2098) — the Portfolio hero and the
// Dashboard hero must state the same figure for the same participant, so neither
// derives its own. The wire's explicit `pendingItems` is preferred; a payload that
// predates the field falls back to inference from activity statuses, as Portfolio does.
import {
  buildBalance, normalizePendingItems, pendingFromActivity, round2,
} from '../../../utils/pending';

/**
 * Period bucket → i18n key. The payload's `label` is an English DATA string
 * ("this paycheck", "last 30 days"), which components would otherwise render
 * untranslated. Attaching the key at the boundary keeps that decision in the
 * adapter (Hard Rule #2) rather than in every consumer; `label` survives as the
 * i18n `defaultValue` so an unrecognised bucket still reads sensibly.
 */
/**
 * Quick-action keys the dashboard does NOT render, however the backend serves them.
 * A frontend-only suppression: the contract, the payload and every other consumer of
 * these actions are untouched, so removing a key here restores its tile.
 */
const HIDDEN_QUICK_ACTIONS = new Set(['rate', 'beneficiaries']);

const PERIOD_LABEL_KEY = {
  Current: 'dashboard.contributions.periodLabel.Current',
  '30-Day': 'dashboard.contributions.periodLabel.30-Day',
  '45-Day': 'dashboard.contributions.periodLabel.45-Day',
  YTD: 'dashboard.contributions.periodLabel.YTD',
};

const withLabelKeys = (periods = {}) => Object.entries(periods).reduce((acc, [k, v]) => {
  acc[k] = { ...v, labelKey: PERIOD_LABEL_KEY[k] ?? null };
  return acc;
}, {});

/**
 * UI → API direction (writes). `org_id` is injected HERE, never in a component
 * (Rule 2). Booleans convert back to 0/1 on the wire.
 */
export const mapUIToAPI = (ui = {}) => ({
  ...ui,
  org_id: localStorage.getItem('pcs_org_id'),
});

// ───────────────────────── Live backend → UI ─────────────────────────
// The /dashboard contract is already camelCase but structured differently from
// the UI model (normalized vs presentation). These small maps translate the
// backend's enum/semantic values into the keys the widgets expect.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// (`fmtUSD` retired with the local sign rule — amounts stay NUMBERS through the
// adapter now and the card formats them, so there is one currency formatter on the
// screen instead of two disagreeing about the sign.)

// (Transaction category → icon now comes from `utils/activity` — it was
// duplicated here, in portfolio.adapters and in RecentActivity, which is how the
// three drifted apart.)
// Backend money-source key → UI brand tone. The contract emits NINE keys, and the
// keys that co-occur must map to distinct tones: ACC-P001-AXOS carries seven sources,
// and with the old five-key map four of them fell back to 'blue' — the same tone as
// pretax — so five of seven segments rendered identically.
const SOURCE_TONE = {
  pretax: 'blue',
  match: 'green',
  roth: 'yellow',
  aftertax: 'purple',
  profitsharing: 'orange',
  safeharbor: 'forest',
  rollover: 'navy',
  paycredit: 'blue',
  interest: 'green',
  // Outstanding participant loan — its own tone so it is never mistaken for a
  // contribution source (it previously fell through to the pretax blue).
  loan: 'orange',
  // Generic employer money and an unrecognised source (FM-2716) — their own tones so
  // neither is read as the participant's pre-tax deferral.
  employer: 'sky',
  other: 'amber',
};
// Wording approved by product on the FM-2716 help-text inventory (2026-10).
const SOURCE_TIP = {
  pretax: 'Your own contributions, deducted before income tax.',
  match: 'Employer contribution that matches your own contributions.',
  roth: 'Tax-free in retirement.',
  aftertax: 'Non-Roth after-tax contributions.',
  profitsharing: 'Discretionary employer contribution, usually allocated once a year.',
  safeharbor: 'Required employer contribution; always fully vested.',
  rollover: 'Money transferred in from a previous employer’s plan.',
  loan: 'Outstanding balance of a loan from your account.',
  paycredit: 'Employer-funded credits added to your cash-balance account.',
  interest: 'Guaranteed interest credited to your cash-balance account.',
  employer: 'Contributed by your employer on your behalf.',
  other: 'A contribution source in your plan. Contact your plan administrator for details.',
};
// Per-source wording where one key covers several sources (FM-2716): a QACA safe-harbor
// contribution can vest over up to two years, unlike the non-elective one.
const QACA_TIP = {
  employer: 'Employer safe-harbor contribution under auto-enrollment; may vest 2 years.',
  match: 'Employer match on your contributions; may vest 2 years.',
};
const SOURCE_LABEL_TIP = {
  'employer qaca': QACA_TIP.employer,
  'match sh qaca': QACA_TIP.match,
  'sh non election': 'Employer contribution regardless of your contributions; always vested.',
  'employer qnec': 'Qualified non-elective employer contribution, made to help the plan pass IRS testing — always fully vested.',
  'qnec': 'Qualified non-elective employer contribution, made to help the plan pass IRS testing — always fully vested.',
  'employer qmac': 'Qualified matching employer contribution, made to help the plan pass IRS testing — always fully vested.',
  'qmac': 'Qualified matching employer contribution, made to help the plan pass IRS testing — always fully vested.',
};
// The Relius source name is free text per plan ("Safe Harbor NE QACA", "QACA Match",
// "SHM QACA"…), so the QACA vesting claim keys off the name pattern, not an exact list:
// the generic safe-harbor tip says "always fully vested", which is false for QACA money.
const sourceTip = (label, sourceKey, vesting) => {
  const low = (label || '').trim().toLowerCase();
  let tip = SOURCE_LABEL_TIP[low];
  if (!tip && /qaca/.test(low)) tip = /match|\bshm\b/.test(low) ? QACA_TIP.match : QACA_TIP.employer;
  if (!tip) tip = SOURCE_TIP[sourceKey] || '';
  // The wire's own vesting row outranks any name-based claim: a source reported as less
  // than fully vested must never be described as "always vested".
  const partlyVested = vesting?.pct != null && vesting.pct < 100;
  return partlyVested && /always/.test(tip) ? SOURCE_TIP.employer : tip;
};

// (`fmtTargetLabel` retired with the milestone journey strip — the refreshed design
// removed that surface from the hero (FM-2116).)
// Backend alert tone/id → GreetingAlerts tone + icon (icons: warn|info|download|users).
const ALERT_TONE = { warn: 'warning', info: 'info' };
const ALERT_ICON = { tax: 'download', match: 'warn', benef: 'users' };
// Map a projection's incomeSources labels → the readiness split keys.
const SPLIT_KEY = { 'This Plan': 'thisPlan', 'Social Security': 'socialSecurity', Other: 'outside' };

// One projection's own income split: share (pct) and iJoin's dollars (monthlyValue) per key.
const splitOf = (sources = []) => sources.reduce((acc, src) => {
  const key = SPLIT_KEY[src.label];
  if (key) {
    acc.split[key] = src.pct;
    acc.sourceDollars[key] = src.monthlyValue;
  }
  return acc;
}, { split: {}, sourceDollars: {} });

/**
 * One wire `RetirementProjection` → the card's per-age shape. Exported because the
 * on-demand age fetch (dashboard.slice `fetchReadinessProjection`) merges single rows
 * into `readiness.projections` and must produce exactly what the page load produced.
 */
export const mapProjection = (pr) => ({
  income: pr.monthlyIncome,
  balance: pr.projectedBalance ?? null,
  // Signed monthly shortfall from iJoin (need − projected; negative = surplus).
  // Branch on the ROUNDED value — at exactly 100% of goal iJoin returns float
  // residue (5e-12), never a clean 0.
  shortfall: pr.shortfall ?? null,
  pct: Math.min(pr.readinessPct, 100), // gauge fill caps at 100
  pctLabel: pr.readinessPct, // actual, may exceed 100
  gaugeTone: pr.tone === '' || pr.tone == null ? 'default' : pr.tone,
  status: pr.status,
  // Per age, and iJoin's own dollars: Social Security and plan income change with the
  // retirement age, and income × a whole-percent share misstated both (FM-3083).
  ...(pr.incomeSources?.length ? splitOf(pr.incomeSources) : {}),
});

const shortDate = (iso) => {
  if (!iso) return '';
  const [, m, d] = String(iso).split('-');
  return m && d ? `${MONTHS[Number(m) - 1]} ${Number(d)}` : iso;
};

const titleCase = (s = '') => s.charAt(0).toUpperCase() + s.slice(1);

// The card shows the last 10 (FRONTEND_RULES §4 — "recent-activity feed (last 10)").
const FEED_LIMIT = 10;

/**
 * Activity rows, normalized through the shared domain layer (`utils/activity`).
 *
 * BOTH boundaries go through this, which is the point: the sign rule, the icon map,
 * origin, amount tiering and the feed cap are decided once. They used to be decided
 * here, and the local rule was `direction === 'neg' ? '-' : '+'` — so ACC-P001-ACME's
 * fund-to-fund transfer (`direction: 'na'`, $2,000) rendered "+$2,000.00", a credit
 * the participant never received. `activityRows` returns `showSign: false` for that
 * case and the card omits the sign.
 *
 * `statusLabel` and `linkLabel` are display copy this adapter doesn't own, so
 * whatever the caller already supplied survives.
 *
 * @param {Array} items wire-ish transaction rows
 * @param {(d: string) => string} [formatDate] applied before normalizing (live sends ISO)
 */
const normalizeActivity = (items = [], formatDate) => {
  const prepared = formatDate
    ? items.map((t) => ({ ...t, date: formatDate(t.date) }))
    : items;
  // `activityRows` slices from the front, so row i still lines up with prepared[i].
  return activityRows(prepared, { limit: FEED_LIMIT }).map((r, i) => ({
    ...r,
    statusLabel: prepared[i].statusLabel ?? titleCase(prepared[i].status || ''),
    linkLabel: prepared[i].linkLabel ?? null,
  }));
};

/**
 * Map the live backend /dashboard payload onto the UI model. Live fields overlay
 * a complete static baseline (the mock through `mapAPIToUI`), so UI-only fields
 * the endpoint doesn't carry (sparklines, holdings, quick actions, tip, etc.)
 * still render. Returns the baseline unchanged when there's no payload.
 * @param {object|null} api raw /dashboard response
 */
export const mapDashboardResponse = (api) => {
  // Built from the PAYLOAD ALONE. This used to start from `mapAPIToUI(DASHBOARD_MOCK)`
  // as a "complete baseline" and overlay live fields on top, which meant any field the
  // endpoint didn't send fell through to Acme's mock values — so every live account
  // rendered Acme's readiness trajectory ("+12 YTD, 79 → 91") and, on nine of ten
  // accounts, Acme's quick-action tiles. Both fields are on the contract now, so the
  // baseline is gone and an absent field reads as absent.
  const ui = {
    person: {}, balance: {}, contributions: {}, holdings: {},
    activity: { net30d: null, items: [] },
    readiness: null, alerts: [], quickActions: [], insights: [],
    distributions: [], distributionSummary: null, statusBanner: null, managedAccount: null, riskTolerance: null,
    tip: null, planStatus: 'active', planName: null, asOf: null,
  };
  if (!api) return ui;

  const { participant: p = {}, account: acct = {}, moneySources = [], balanceGrowthBreakdown: bgb,
    projections = [], contributions = [], activity = [], alerts = [],
    balanceTrend, holdings } = api;

  // ── person ──
  if (p.firstName) {
    // Warehouse full name when present (can carry a middle name); joined first/last otherwise.
    const fullName = p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ');
    ui.person = {
      ...ui.person,
      firstName: p.firstName,
      fullName,
      initials: `${p.firstName?.[0] || ''}${p.lastName?.[0] || ''}`.toUpperCase(),
    };
  }

  // ── balance headline ──
  // Employer short name: drop the legal tail (", Inc." etc.) rather than taking the
  // first word — "A Penny Saved, Inc." was shortening to "A", so its growth row read
  // "A matched". BACKEND TODO: a real shortName on the account would retire this.
  const employerShort = (acct.employerName || '').split(',')[0].trim()
    .replace(/\s+(Corporation|Incorporated|Company|Group|Holdings|LLC|Inc|Corp|Co)\.?$/i, '')
    || 'Employer';
  ui.planName = acct.planName ?? null;
  ui.balance = {
    ...ui.balance,
    accountBalance: acct.totalBalance ?? 0,
    vestedBalance: acct.vestedBalance ?? acct.totalBalance ?? 0,
    // Absent = unknown (trend too short); the hero shows a placeholder, never +$0.00.
    dayChangeAmount: acct.dayChangeAmount ?? null,
    dayChangePct: acct.dayChangePct ?? null,
    // The plan-level gate: the inactive plan sends false and gets no vesting strip —
    // vesting language is meaningless where nothing further can vest.
    showVesting: acct.showVesting ?? true,
  };
  // Milestone journey — REMOVED (FM-2116). The refreshed design dropped the strip
  // entirely (`vstrip-journey` survives only in the legacy dashboard.jsx); the hero's
  // eyebrow carries the tenure pill instead. Nulled explicitly so the mock baseline's
  // "95% to $300K" can never leak back in. `nextMilestone*` stays on the contract.
  ui.balance.milestone = null;
  // The employer name the hero interpolates into its growth-row label
  // ("{Employer} contributed") and tooltips.
  ui.balance.employer = employerShort;
  // Pending — the contract's EXPLICIT `pendingItems` when present (the design states
  // these per plan, including the null-amount net-zero rebalance), falling back to
  // inference from activity statuses for a payload that predates the field. Same
  // items back /portfolio's hero (FM-2098).
  //
  // DESIGN SEMANTICS (FM-2116): the headline balance ALREADY INCLUDES in-flight money
  // — the strip says "$N in your balance", not "$N on top". So `total` must equal
  // `account.totalBalance` exactly, and `settled` is what remains under it.
  {
    const pendingItems = api.pendingItems != null
      ? normalizePendingItems(api.pendingItems)
      : pendingFromActivity(activity);
    const net = round2(pendingItems.reduce((sum, i) => sum + (i.amount || 0), 0));
    ui.balance.pending = buildBalance(
      acct.totalBalance == null ? null : round2(acct.totalBalance - net),
      acct.asOfDate ?? null,
      pendingItems,
    );
  }
  // Tenure pill (FM-2116) — display strings straight off the contract; month
  // precision on young accounts ("Nov 2024 · 1 yr 8 mo") is the backend's statement,
  // never derived here. Nulled when absent so no plan inherits another's hire date.
  ui.balance.tenure = (acct.tenureSince || acct.tenureDuration)
    ? { since: acct.tenureSince ?? null, duration: acct.tenureDuration ?? null }
    : null;
  // Plan status gates every inactive-plan surface. The backend ships participant
  // employment status; a plan-level status is a BACKEND TODO, so until it exists a
  // terminated participant is the only non-active state we can honestly detect.
  if (acct.planStatus || acct.employmentStatus) {
    ui.planStatus = acct.planStatus ?? (acct.employmentStatus === 'terminated' ? 'terminated' : 'active');
  }
  // Trend chart — live per-account series/labels/deltas (was static). Each ends at
  // the account's real balance, so the chart is scaled to the selected plan.
  // Empty objects rather than absent keys: the hero reads `Object.keys(sparkSeries)` and
  // indexes the rest by period, so an account with no trend block must still find
  // something iterable there.
  ui.balance.sparkSeries = balanceTrend?.series ?? {};
  ui.balance.sparkDates = balanceTrend?.dates ?? {}; // FM-1772: per-point ISO date, keyed by period
  ui.balance.sparkCadence = balanceTrend?.cadence ?? {}; // period → weekly|monthly|quarterly|yearly
  ui.balance.sparkLabels = balanceTrend?.labels ?? {};
  ui.balance.periodDeltas = balanceTrend?.deltas ?? {};
  // Per-plan chart config (FM-2116) — unconditional, so no plan inherits another's:
  // disabled periods render INERT (muted, not removed); `returnStat` picks the footer
  // treatment (period | inception | interestCredit); `chartLayers` unlocks the
  // composition view. (`stackedLine` is still read through; the hero no longer offers
  // that view, or the passbook one.)
  ui.balance.sparkDisabled = balanceTrend?.disabled ?? [];
  ui.balance.sparkDefaultPeriod = balanceTrend?.defaultPeriod ?? null;
  ui.balance.returnStat = balanceTrend?.returnStat ?? null;
  ui.balance.chartLayers = balanceTrend?.layers ?? null;
  // Growth rows (FM-2116) — per-row IDENTITY from the wire (`key`/`tone`/`contrib`);
  // the component localizes the label, so no English is synthesized here any more
  // ("{Employer} matched" was both untranslatable and the wrong verb — the design
  // says "contributed", and a cash-balance plan's rows are credits, not a match).
  // Falls back to the three back-compat scalars keyed the same way.
  {
    const rows = api.balanceGrowthRows?.length
      ? api.balanceGrowthRows.map((r) => ({
        key: r.key, value: r.value, tone: r.tone, contrib: r.contrib === true,
      }))
      : bgb
        ? [
          { key: 'you', value: bgb.youContributedYtd, tone: 'blue', contrib: true },
          { key: 'employer', value: bgb.employerMatchedYtd, tone: 'green', contrib: true },
          { key: 'market', value: bgb.marketGrowthYtd, tone: 'yellow', contrib: false },
        ]
        : [];
    // A plan with no payroll feed has contributed nothing this year, and the wire says
    // so by sending those rows at 0. Rendering them anyway produced "You contributed
    // +$0" and "{Employer} contributed +$0" on the inactive plan — technically true,
    // but it invites the participant to look for money that structurally cannot
    // arrive. The design shows market gain alone here, so drop ZERO-VALUED
    // contribution rows once payroll has stopped. A zero row on an ACTIVE plan is kept:
    // there, "you contributed $0 so far" is a real and actionable statement.
    const noPayroll = acct.employmentStatus === 'terminated' || api.readiness?.type === 'growthProjection';
    const shown = noPayroll ? rows.filter((r) => !(r.contrib && !r.value)) : rows;
    ui.balance.growthBreakdown = shown;
    // The headline still totals every row — dropping a $0 row cannot change the sum,
    // and totalling only the shown rows would silently diverge if a nonzero row were
    // ever filtered.
    ui.balance.ytdChange = rows.length
      ? round2(rows.reduce((sum, r) => sum + (r.value || 0), 0))
      : null;
  }
  if (moneySources.length) {
    // A source holding $0.00 is hidden, as Classic does (FM-3064).
    const held = moneySources.filter((s) => Math.round((s.balance || 0) * 100) !== 0);
    // Whole-number shares from the balances that add to exactly 100% (FM-3056).
    const shown = wholePercents(held.map((s) => s.balance));
    ui.balance.sources = held.map((s, i) => ({
      key: s.sourceKey,
      label: s.label,
      pct: shown[i],
      // The EXACT balance off the wire — the legend/modal must state it rather than
      // recompute `total × pct/100`, which was off by $63.50 a row on the cash-balance
      // plan (whole-percent rounding masquerading as money).
      amount: s.balance ?? null,
      tone: SOURCE_TONE[s.sourceKey] || 'blue',
      // Vesting is a property of the SOURCE, not of the account — one plan can hold an
      // employer source behind a cliff next to employee sources vested on arrival.
      // Absent on the wire = fully vested (the contract's rule); the breakdown modal
      // renders "Fully vested" for null rather than inventing a schedule.
      vesting: s.vesting
        ? { type: s.vesting.type ?? 'graded', pct: s.vesting.pct ?? null, schedule: s.vesting.schedule ?? null }
        : null,
      tipExtra: sourceTip(s.label, s.sourceKey, s.vesting),
    }));
  }

  // ── readiness block — previously dropped wholesale ──
  // `type` is the card discriminator (ijoin | calculator | growthProjection): which
  // readiness surface this account gets is the backend's call, not the frontend's.
  // `monthlyNeeded` is the account's real income goal — the card's goal figure was
  // silently the mock's static 6500 for every plan until this mapped through.
  if (api.readiness) {
    const r = api.readiness;
    ui.readiness = {
      type: r.type ?? 'ijoin',
      goalMonthly: r.monthlyNeeded ?? null,
      monthlyProjected: r.monthlyProjected ?? null,
      goalPct: r.goalPct ?? null,
      projectedLumpSum: r.projectedLumpSum ?? null,
      assumedGrowthRate: r.assumedGrowthRate ?? null,
      editable: r.editable ?? false,
      note: r.note ?? null,
      consolidationNudge: r.consolidationNudge ?? null,
      // Trajectory chip. Absent → the card hides the chip, which is why these are
      // passed through as-is rather than defaulted to a number.
      score: r.score ?? null,
      scoreDeltaYtd: r.scoreDeltaYtd ?? null,
      scoreBaseline: r.scoreBaseline ?? null,
      scoreBaselineMonth: r.scoreBaselineMonth ?? null,
      incomeSourceSplit: {},
      projections: {},
    };
  }

  // ── readiness projections ──
  if (api.readiness?.retirementAges?.length) {
    // The ages the card may OFFER. Only the default age's projection ships with the
    // page; the rest are fetched when picked (fetchReadinessProjection), so the picker
    // must not be limited to the projections already loaded.
    ui.readiness.retirementAges = api.readiness.retirementAges.map(String);
  }
  if (projections.length) {
    ui.readiness = { ...(ui.readiness || {}), ageToday: p.age ?? null };
    ui.readiness.projections = projections.reduce((acc, pr) => {
      acc[pr.retirementAge] = mapProjection(pr);
      return acc;
    }, {});
    // Income split is the same across ages; read it from the 65 projection (or first).
    const splitSrc = projections.find((pr) => pr.retirementAge === 65) || projections[0];
    if (splitSrc?.incomeSources?.length) {
      ui.readiness.incomeSourceSplit = splitSrc.incomeSources.reduce((acc, src) => {
        const key = SPLIT_KEY[src.label];
        if (key) acc[key] = src.pct;
        return acc;
      }, {});
    }
  }

  // ── contributions ──
  // The /dashboard payload owns the PERIOD TOTALS only. Per-source rates, the
  // applicable IRS limit, match confidence and the auto-increase capability flags
  // live on /manage-account (`contributionSetup`) and are merged in by the module
  // hook — see `useDashboardContributions` in dashboard.hooks.js.
  // Employer name threaded through so the card stops hardcoding "Acme".
  ui.contributions.employer = employerShort;
  // FM-1918 — a terminated participant has no payroll feed, so the whole widget
  // is suppressed and the neighbouring card takes the row.
  ui.contributions.employmentStatus = acct.employmentStatus ?? null;
  if (contributions.length) {
    ui.contributions.periods = withLabelKeys(contributions.reduce((acc, c) => {
      // `you`/`match` may be null: the warehouse's 30-day window is a total with no
      // source split. `total` is the wire's figure when sent, else the sum of the halves.
      const you = c.youAmount ?? null;
      const match = c.matchAmount ?? null;
      const total = c.totalAmount ?? ((you ?? 0) + (match ?? 0));
      // Per-period as-of (30-Day = the window's last day); null when the wire has none.
      // `available: false` = the bucket is offered but has no figure yet (the warehouse
      // can't attribute a pay period); the card shows a not-available state, not $0.
      acc[c.period] = {
        you, match, total, label: c.label, asOf: c.asOf ?? null, available: c.available !== false,
        derived: c.derived === true,
      };
      return acc;
    }, {}));
    const ytd = contributions.find((c) => c.period === 'YTD');
    if (ytd) {
      ui.contributions.eeYtd = ytd.youAmount;
      ui.contributions.erYtd = ytd.matchAmount;
    }
  }

  // ── activity ──
  // `normalizeActivity` owns sign / tone / icon / origin / tiering / cap; only the
  // display copy is decided here. `linkKind` on each row is the i18n-ready form of
  // the link label and is what should replace this English once the card takes it.
  if (activity.length) {
    // Net 30D — computed from the rows, never inherited: the baseline's "+$448.75"
    // is Acme mock arithmetic and was rendering on every plan. Only rows that MOVED
    // money count (a transfer's `na` direction is net-zero by definition), windowed
    // to the 30 days before the account's as-of date. No dated rows in the window →
    // no figure → the card hides the pill, which is true rather than fictional.
    const asOfMs = Date.parse(acct.asOfDate);
    const windowStart = asOfMs - 30 * 24 * 60 * 60 * 1000;
    let net = null;
    if (Number.isFinite(asOfMs)) {
      const inWindow = activity.filter((t) => {
        const ts = Date.parse(t.date);
        return t.amount != null && (t.direction === 'pos' || t.direction === 'neg')
          && Number.isFinite(ts) && ts >= windowStart && ts <= asOfMs;
      });
      if (inWindow.length) {
        const sum = inWindow.reduce((s, t) => s + (t.direction === 'neg' ? -t.amount : t.amount), 0);
        net = `${sum < 0 ? '-' : '+'}$${Math.abs(sum).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    }
    ui.activity = {
      net30d: net,
      items: normalizeActivity(
        activity.map((t) => ({
          ...t,
          id: t.transactionId,
          detail: t.detail || (t.confirmationNumber ? `Confirmation ${t.confirmationNumber}` : ''),
          linkLabel: ['contrib', 'match', 'div'].includes(t.category) ? 'View receipt' : 'View details',
        })),
        shortDate,
      ),
    };
  }

  // ── distributions ──
  // Taken from the payload alone, never inherited: the mock baseline carries Acme's
  // five in-flight requests — without this line EVERY live account showed Acme's
  // fictional DST-2026-* tracker. Absent means "none in flight".
  ui.distributions = api.distributions ?? [];
  // Aggregate distribution history (count / total / last). The live source has this but
  // no per-request rows yet, so the status card shows the summary plus recent fund
  // activity in place of the pipeline. Absent → the account has never distributed.
  ui.distributionSummary = api.distributionSummary
    ? {
        count: api.distributionSummary.count,
        total: api.distributionSummary.total,
        lastDate: api.distributionSummary.lastDate ? shortDate(api.distributionSummary.lastDate) : null,
        lastAmount: api.distributionSummary.lastAmount ?? null,
      }
    : null;

  // ── status banner (inactive/non-standard plans only) ──
  ui.statusBanner = api.statusBanner ?? null;

  // ── hero right-rail cards — from the payload ALONE, never inherited ──
  // BACKEND TODO: /dashboard serves neither block yet. `ManagedAccount`
  // (provider/providerKey) and `RiskTolerance` do exist on /manage-account — wiring
  // them onto this endpoint is what makes the per-plan hero right rail reachable in
  // live mode.
  ui.managedAccount = api.managedAccount ?? null;
  ui.riskTolerance = api.riskTolerance ?? null;
  // A plan with neither a readiness block nor projections has no readiness surface at
  // all (Demo Axos by design) — the hero right rail then falls through to
  // riskTolerance (layout B) or to a single column.
  if (!api.readiness && !projections.length) ui.readiness = null;

  // ── quick actions ──
  // The permitted set is whatever the payload lists — presence means permitted, absence
  // means not rendered. There is no frontend default: the previous one was the Acme
  // mock's six tiles, which nine of ten live accounts silently inherited, offering
  // "Change rate" and "Transfer funds" on plans that may forbid both.
  // FRONTEND-ONLY SUPPRESSION: "Change rate" (the recommended tile) and "Beneficiaries"
  // are hidden from the dashboard by product decision. The backend still serves both —
  // drop this filter to bring them back; nothing else needs to change.
  if (api.quickActions?.length) {
    const tiles = api.quickActions
      .filter((a) => !HIDDEN_QUICK_ACTIONS.has(a.key))
      .map((a) => ({
        icon: a.key,
        title: a.label,
        tone: a.recommended ? 'primary' : 'neutral',
        featured: a.recommended === true,
      }));
    ui.quickActions = [...tiles.filter((x) => x.featured), ...tiles.filter((x) => !x.featured)];
  }

  // ── insights → smart tip ──
  // Per-account insight text replaces the baseline tip, which was Acme's "raise your
  // deferral 2%" advice rendering on a cash-balance plan (no deferral to raise) and an
  // inactive plan (no payroll at all). The tip carries no CTAs from the payload —
  // baseline CTAs belonged to Acme's copy, and inventing new ones here would be
  // English in the adapter. A `question`-kind insight is a R.I.S.A. prompt (routing
  // TODO); `insight` kinds fill heading/body in order.
  ui.insights = api.insights ?? [];
  if (api.insights) {
    const texts = api.insights.filter((i) => i.kind === 'insight').map((i) => i.text);
    ui.tip = texts.length
      ? { heading: texts[0], body: texts[1] ?? '', primaryCta: null, secondaryCta: null }
      : null;
  }

  // ── alerts ──
  if (alerts.length) {
    ui.alerts = alerts.map((a, i) => ({
      id: a.alertId,
      tone: ALERT_TONE[a.tone] || 'info',
      priority: i + 1,
      icon: ALERT_ICON[a.alertId] || (a.tone === 'warn' ? 'warn' : 'info'),
      heading: a.heading,
      body: a.body,
      cta: a.ctaLabel,
    }));
  }

  // ── holdings glimpse — live per-account (was static). Backend shape already
  // matches the UI: { totalFunds, totalAmount, ytdPct, benchmarkPct,
  // benchmarkApplicable, allocation, funds }.
  //
  // The benchmark pair is explicitly nulled when absent rather than left undefined:
  // an account the backend sends no benchmark for (a cash-balance plan) must not show
  // a comparison at all. It used to inherit the Acme mock's 9.2 and tell that
  // participant they were behind an index that was never theirs — showing none is
  // right where showing the wrong one is a defect.
  if (holdings) {
    ui.holdings = {
      ...holdings,
      benchmarkPct: holdings.benchmarkPct ?? null,
      benchmarkApplicable: holdings.benchmarkApplicable ?? null,
    };
  }
  // The as-of day belongs to the ACCOUNT, not to any one block — every pill on the
  // screen reads the same value so the hero, Contributions and Holdings can't report
  // three different dates for one account.
  ui.asOf = acct.asOfDate ?? ui.asOf ?? null;
  ui.balance = { ...ui.balance, asOf: ui.asOf };
  ui.holdings = { ...ui.holdings, asOf: ui.asOf };
  ui.contributions = { ...ui.contributions, asOf: ui.asOf };

  return ui;
};

/**
 * GET /dashboard/distributions/{id} → the DistributionDetailsModal's record. The wire
 * is already camelCase and the modal prints the recordkeeper's own strings (payment
 * labels, types, dates), so this normalises shape rather than renames: every list is
 * an array, every optional block is null-or-object, numbers are numbers.
 */
export const mapDistributionDetails = (api) => {
  if (!api) return null;
  const num = (v) => (v == null || v === '' ? null : Number(v));
  const loan = api.loan
    ? {
        sponsorName: api.loan.sponsorName ?? null,
        planName: api.loan.planName ?? null,
        employeeId: api.loan.employeeId ?? null,
        employeeName: api.loan.employeeName ?? null,
        initialAmount: num(api.loan.initialAmount),
        currentBalance: num(api.loan.currentBalance),
        effectiveDate: api.loan.effectiveDate ?? null,
        action: api.loan.action ?? null,
        interestRate: num(api.loan.interestRate),
        frequency: api.loan.frequency ?? null,
        principalPlusInterest: num(api.loan.principalPlusInterest),
        schedule: (api.loan.schedule ?? []).map((r) => ({
          n: Number(r.n),
          date: r.date ?? '',
          amount: Number(r.amount) || 0,
          interest: Number(r.interest) || 0,
          principal: Number(r.principal) || 0,
          balance: num(r.balance),
        })),
        total: api.loan.total
          ? { amount: Number(api.loan.total.amount) || 0, interest: Number(api.loan.total.interest) || 0, principal: Number(api.loan.total.principal) || 0 }
          : null,
      }
    : null;
  return {
    requestId: api.requestId,
    type: api.type ?? '',
    status: api.status ?? 'in-progress',
    requestedDate: api.requestedDate ?? null,
    requestedAmount: Number(api.requestedAmount) || 0,
    confirmationNumber: api.confirmationNumber ?? null,
    message: api.message ?? null,
    hasDetails: !!api.hasDetails,
    payments: (api.payments ?? []).map((p, i) => ({
      key: String(p.key ?? i + 1),
      label: p.label ?? `Payment ${i + 1}`,
      payee: p.payee ?? null,
      company: p.company ?? null,
      paymentType: p.paymentType ?? null,
      sourceDescription: p.sourceDescription ?? null,
      addressLines: p.addressLines ?? [],
      gross: num(p.gross),
      fee: num(p.fee),
      federalTax: num(p.federalTax),
      stateTax: num(p.stateTax),
      additionalFederalTax: num(p.additionalFederalTax),
      additionalStateTax: num(p.additionalStateTax),
      loanPayoffAmount: num(p.loanPayoffAmount),
      net: num(p.net), // null until the request reaches Payment
    })),
    loan,
  };
};
